/**
 * PortfolioScorecard — shared display for /plan-portfolio + public sibling.
 *
 * Props:
 *   data:   { portfolio:{...}, coherence:{...} }
 *   testidPrefix: 'pp' | 'pphc' etc.
 */

const LEVEL_LABEL = { 1: "Ad hoc", 2: "Repeatable", 3: "Defined", 4: "Managed", 5: "Optimising" };
const LEVEL_COLOR = {
  1: "bg-rose-500",
  2: "bg-orange-400",
  3: "bg-amber-400",
  4: "bg-emerald-500",
  5: "bg-emerald-600",
};

function levelFromScore(s) {
  if (s >= 0.85) return 5;
  if (s >= 0.7) return 4;
  if (s >= 0.5) return 3;
  if (s >= 0.3) return 2;
  return 1;
}

export function PortfolioScorecard({ data, testidPrefix = "pp" }) {
  if (!data) return null;
  const p = data.portfolio;
  const c = data.coherence || {};
  const aiProvenance = c.ai_dependency_suggestions?.provenance || "not-requested";

  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-scorecard`}>
      {/* Headline */}
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold">
          Plan Portfolio Integrity · v{p.engine_version}
        </p>
        <div className="mt-2 flex items-baseline gap-3">
          <h2 className="text-4xl font-bold tracking-tight text-violet-900" data-testid={`${testidPrefix}-overall-level`}>
            Level {p.overall_level}
          </h2>
          <span className="text-xl font-semibold text-slate-700">{p.overall_level_label}</span>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Composite <strong>{p.overall_score.toFixed(3)}</strong> · Confidence <strong>{p.confidence}</strong> · {p.workstream_count} workstream(s) · {p.total_items} plan item(s) scored
        </p>
        {p.best_plan && p.worst_plan && p.best_plan.name !== p.worst_plan.name && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg bg-emerald-50/60 border-l-4 border-emerald-400 px-3 py-2 text-sm" data-testid={`${testidPrefix}-best`}>
              <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Strongest workstream</p>
              <p className="mt-0.5 font-medium text-slate-800">{p.best_plan.name} · L{p.best_plan.level} · {p.best_plan.score.toFixed(2)}</p>
            </div>
            <div className="rounded-lg bg-rose-50/60 border-l-4 border-rose-400 px-3 py-2 text-sm" data-testid={`${testidPrefix}-worst`}>
              <p className="text-xs uppercase tracking-wide text-rose-700 font-semibold">Weakest workstream</p>
              <p className="mt-0.5 font-medium text-slate-800">{p.worst_plan.name} · L{p.worst_plan.level} · {p.worst_plan.score.toFixed(2)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Systemic gaps */}
      {p.systemic_gaps?.length > 0 && (
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200" data-testid={`${testidPrefix}-systemic`}>
          <h3 className="text-lg font-semibold text-violet-900">Programme-wide failure patterns</h3>
          <p className="text-xs text-slate-500 mb-3">Checks where ≥40% of workstreams fail (Level ≤3). These are systemic issues, not per-plan accidents.</p>
          <ul className="space-y-3">
            {p.systemic_gaps.map((g) => (
              <li key={g.check_id} className="rounded-lg border-l-4 border-violet-400 bg-violet-50/40 px-4 py-3" data-testid={`${testidPrefix}-systemic-${g.check_id}`}>
                <p className="font-medium text-slate-800">{g.name} · {g.pmo360_dimension}</p>
                <p className="text-xs text-slate-600 mt-0.5">{g.verdict}</p>
                <p className="text-[11px] text-slate-500 mt-1">Failing workstreams: {g.workstreams_failing.join(", ")}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-workstream breakdown */}
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200" data-testid={`${testidPrefix}-perplan`}>
        <h3 className="text-lg font-semibold text-violet-900">Per-workstream breakdown</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2">Workstream</th>
                <th className="py-2">Items</th>
                <th className="py-2">Score</th>
                <th className="py-2">Level</th>
                <th className="py-2">Worst check</th>
              </tr>
            </thead>
            <tbody>
              {p.per_plan.map((row) => {
                const lvl = row.scorecard.overall_level;
                const worst = (row.scorecard.checks || []).reduce(
                  (a, b) => (a && a.score <= b.score ? a : b), null);
                return (
                  <tr key={row.name} className="border-b border-slate-100" data-testid={`${testidPrefix}-row-${row.name}`}>
                    <td className="py-2 font-medium text-slate-800">{row.name}</td>
                    <td className="py-2 text-slate-600">{row.item_count}</td>
                    <td className="py-2 text-slate-700">{row.scorecard.overall_score.toFixed(2)}</td>
                    <td className="py-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white ${LEVEL_COLOR[levelFromScore(row.scorecard.overall_score)]}`}>
                        L{lvl} {LEVEL_LABEL[lvl]}
                      </span>
                    </td>
                    <td className="py-2 text-xs text-slate-500">
                      {worst ? `${worst.name} (${(worst.score * 100).toFixed(0)}%)` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cross-plan coherence */}
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200" data-testid={`${testidPrefix}-coherence`}>
        <h3 className="text-lg font-semibold text-violet-900">Cross-plan coherence</h3>
        <p className="text-xs text-slate-500 mb-3">
          Contradictions between workstreams that single-plan analysis cannot catch.
        </p>

        {c.shared_owner_conflicts?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Shared-owner conflicts</p>
            <ul className="mt-1 space-y-1 text-sm">
              {c.shared_owner_conflicts.map((x) => (
                <li key={x.owner} className="rounded bg-amber-50/60 border-l-2 border-amber-400 px-3 py-2" data-testid={`${testidPrefix}-owner-conflict`}>
                  {x.verdict}
                </li>
              ))}
            </ul>
          </div>
        )}

        {c.dependency_mismatches?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Dependency mismatches</p>
            <ul className="mt-1 space-y-1 text-sm">
              {c.dependency_mismatches.map((x, i) => (
                <li key={i} className="rounded bg-rose-50/60 border-l-2 border-rose-400 px-3 py-2" data-testid={`${testidPrefix}-dep-mismatch`}>
                  {x.verdict}
                </li>
              ))}
            </ul>
          </div>
        )}

        {c.owner_overallocations?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Owner over-allocations</p>
            <ul className="mt-1 space-y-1 text-sm">
              {c.owner_overallocations.map((x) => (
                <li key={x.owner} className="rounded bg-sky-50/60 border-l-2 border-sky-400 px-3 py-2" data-testid={`${testidPrefix}-overalloc`}>
                  {x.verdict}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* AI-suggested dependencies (opt-in only) */}
        {c.infer_dependencies_enabled && (
          <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50/30 p-3" data-testid={`${testidPrefix}-ai-deps`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">AI-suggested dependencies · please confirm</p>
            <p className="text-[11px] text-slate-500 mb-2">
              Defensible AI Charter: provenance = <strong>{aiProvenance}</strong>. Every suggestion below is a candidate only — never a fact.
            </p>
            {c.ai_dependency_suggestions?.suggestions?.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {c.ai_dependency_suggestions.suggestions.map((s, i) => (
                  <li key={i} className="rounded bg-white px-3 py-2 border border-violet-100" data-testid={`${testidPrefix}-ai-dep-${i}`}>
                    <p className="font-medium text-slate-800">
                      {s.from_plan_id} → {s.to_plan_id} <span className="text-xs text-slate-500">({s.kind})</span>
                      <span className="ml-2 text-xs text-violet-700">confidence {s.confidence}%</span>
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">{s.reason}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500">No high-confidence cross-plan dependencies detected.</p>
            )}
          </div>
        )}

        {!c.shared_owner_conflicts?.length && !c.dependency_mismatches?.length && !c.owner_overallocations?.length && !c.infer_dependencies_enabled && (
          <p className="text-sm text-slate-500">No cross-plan contradictions detected.</p>
        )}
      </div>
    </div>
  );
}
